import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Stepper,
  Step,
  StepLabel,
  Typography,
  makeStyles,
  Box,
  Paper,
  Button,
  Snackbar,
  IconButton,
  CircularProgress,
} from "@material-ui/core";
import MuiAlert from "@material-ui/lab/Alert";
import CloseIcon from "@material-ui/icons/Close";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import ProductDetails from "./steps/ProductDetails";
import StatusSelection from "./steps/StatusSelection";
import ReviewAndAddProduct from "./steps/ReviewAndAddProduct";
import { useLocation, useHistory, useParams } from "react-router-dom";
import { useIsMounted } from "../../../../../hooks/useIsMounted";
import axios from "axios";
import { appUrl } from "../../../../../../../utils/config";

// Fetch FG Statuses API function (same as Commercial Supply)
const getFetchFGStatusesAPI = (apiBaseUrl) => {
  return async () => {
    if (!apiBaseUrl) return null;
    try {
      const response = await axios.get(`${apiBaseUrl}/fetchFGStatuses/true`);
      if (response?.data?.data) {
        return response.data.data;
      }
      return null;
    } catch (error) {
      console.error("Error fetching FG statuses:", error);
      return null;
    }
  };
};

// Session Storage Keys - Product Development specific
const SESSION_KEYS = {
  CURRENT_STEP: "pdCurrentStep",
  FORM_DATA: "pdProductConfigFormData",
  COMPLETED_STEPS: "pdCompletedSteps",
  SELECTION_FOR_NEW_PRODUCT: "pdSelectionForNewProduct",
  PRODUCT_ADDED_ONCE: "pdProductAddedOnce",
  EMPTY_FG_STATUSES: "pdEmptyFgStatuses",
  SELECTED_REPLICATED_FROM_PRODUCT_ID: "pdSelectedReplicatedFromProductID",
};

// Helper function to check if product is a file uploaded product
function checkFileUploadedProduct(productFormData) {
  const hasMaterialProducts = productFormData?.materialDetails?.productMaterialsList && 
                             productFormData.materialDetails.productMaterialsList.length > 0;
  const isFgStatusesEmpty = !productFormData?.productFGStatuses?.fgStatusesJSON || 
                           Object.keys(productFormData.productFGStatuses.fgStatusesJSON).length === 0;
  return hasMaterialProducts && isFgStatusesEmpty;
}

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
  },
  stepper: {
    maxWidth: "90%",
    margin: "0 auto",
    padding: "50px 24px",
  },
  stepCompleted: {
    color: "#4CAF50",
  },
  backButton: {
    display: "flex",
    alignItems: "center",
    color: "#ec6535",
    marginBottom: "1rem",
    marginTop: "1rem",
    cursor: "pointer",
    "&:hover": {
      textDecoration: "underline",
    },
  },
  formTitle: {
    marginBottom: theme.spacing(4),
    fontWeight: 500,
  },
  button: {
    minWidth: 180,
    borderRadius: 30,
  },
  formField: {
    width: "100%",
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#fff",
    },
  },
  select: {
    width: "100%",
    marginTop: theme.spacing(1),
  },
  tableCell: {
    "& .MuiSelect-root": {
      backgroundColor: "#fff",
    },
    padding: theme.spacing(1),
  },
  stepperHeader: {
    padding: "32px 0",
    textAlign: "center",
    backgroundColor: "#F5F9FF",
    position: "relative",
  },
  heading: {
    fontWeight: 500,
    fontSize: 30,
    lineHeight: "100%",
    marginBottom: 20,
  },
  subheading: {
    fontWeight: 400,
    fontSize: 16,
    lineHeight: "100%",
  },
  stepLabel: {
    flexDirection: "column",
    fontSize: 15,
    position: "relative",
    "& .MuiStepLabel-iconContainer": {
      padding: 0,
    },
    "& .MuiStepLabel-labelContainer": {
      width: "auto",
      minWidth: 140,
      position: "absolute",
      left: "50%",
      top: 30,
      transform: " translateX(-50%)",
      textAlign: "center",
    },
  },
}));

const steps = [
  "Product Details",
  "Status Selection",
  "Review And Add Product",
];

class ClinicalProductDevelopmentErrorBoundary extends React.Component {
  state = { hasError: false, error: null };

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error("ClinicalProductDevelopment Error:", error, errorInfo);
  }
  render() {
    if (this.state.hasError) {
      return (
        <Box p={3} textAlign="center">
          <Typography color="error">
            An error occurred while loading the product configuration. Please
            try again.
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              this.setState({ hasError: false });
              sessionStorage.setItem(SESSION_KEYS.CURRENT_STEP, "0");
              window.location.reload();
            }}
            style={{ marginTop: 16 }}
          >
            Retry
          </Button>
        </Box>
      );
    }
    return this.props.children;
  }
}

const ClinicalProductDevelopmentStepper = () => {
  const classes = useStyles();
  const location = useLocation();
  const history = useHistory();
  const isMounted = useIsMounted();
  const { roleName, organizationName, ecosystemId } = useParams();
  const [skipReplicateDialog, setSkipReplicateDialog] = useState(false);

  // State initialization with safety defaults
  const [activeStep, setActiveStep] = useState(0);
  const [isInitialized, setIsInitialized] = useState(false);
  const [hasErrors, setHasErrors] = useState(false);
  const [readOnly, setReadOnly] = useState(false);
  const [savedSteps, setSavedSteps] = useState(new Set());
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const selectedSupplytype = location.state?.selectedSupplytype;
  const [formData, setFormData] = useState({});

  // API base URL
  const apiBaseUrl = useMemo(() => {
    const appName = localStorage.getItem("appName");
    const network = localStorage.getItem("network");
    const collabType = localStorage.getItem("collabType");
    const roleName = localStorage.getItem("roleName");
    if (appName && network && collabType && roleName) {
      return `${appUrl}/${appName}/${network}/${collabType}/${roleName}`;
    }
    return null;
  }, []);

  // Load persisted form data when component mounts - matching Commercial Supply exactly
  useEffect(() => {
    try {
      // Check if we have a selected product from the previous screen
      const selectedProduct = location.state?.selectedProduct;
      const selectedProductFormData = location.state?.selectedProductFormData;

      if (selectedProductFormData) {
        // Check for file uploaded product: materialDetails has products AND productFGStatuses is empty
        const isFileUploadedProduct = checkFileUploadedProduct(selectedProductFormData);
        
        // Set file uploaded product flag in localStorage
        if (isFileUploadedProduct) {
          localStorage.setItem("fileUploadedProduct", "true");
        } else {
          localStorage.removeItem("fileUploadedProduct");
        }
        
        // Set form data from the API response
        setFormData({
          productDetails: selectedProductFormData?.productDetails,
          materialDetails: selectedProductFormData?.materialDetails || null,
          fgStatuses: selectedProductFormData?.productFGStatuses?.fgStatusesJSON || {},
        });
      } 
      else if (location.state?.productDetails && location.state?.materialDetails) {
        // Handle case where productDetails and materialDetails are passed separately (from ClinicalSupply)
        setFormData({
          productDetails: location.state.productDetails,
          materialDetails: location.state.materialDetails,
          fgStatuses: {},
        });
      } 
      else if (selectedProduct) {
        // Update the form data with the selected product info
        setFormData((prevData) => ({
          ...prevData,
          productCode: selectedProduct.sponsorProductCode,
          productDescription: selectedProduct.sponsorProductDescription,
        }));
      }

      // Restore current step - matching Commercial Supply (line 298-305)
      const savedStep = sessionStorage.getItem(SESSION_KEYS.CURRENT_STEP);
      if (savedStep !== null) {
        const stepIndex = parseInt(savedStep, 10);
        if (!isNaN(stepIndex) && stepIndex >= 0 && stepIndex < steps.length) {
          setActiveStep(stepIndex);
        }
      }

      setIsInitialized(true);
    } catch (error) {
      console.error("Error loading persisted form data:", error);
      setHasErrors(true);
    }
  }, [location.state]); // Add location.state as dependency

  // On mount, restore formData from sessionStorage if it exists
  // This runs AFTER location.state useEffect, so sessionStorage data overwrites API data (matching Commercial Supply line 314-319)
  useEffect(() => {
    const savedFormData = sessionStorage.getItem(SESSION_KEYS.FORM_DATA);
    if (savedFormData) {
      try {
        setFormData(JSON.parse(savedFormData));
      } catch (error) {
        console.error("Error parsing saved form data:", error);
      }
    }
  }, []);

  const isEdit = location.state?.isEdit;
  const isPending = location.state?.isPending;
  const selectedProductFormData = location.state?.selectedProductFormData;

  // Handle pending product edit - only determine step if no saved step exists
  // This ensures that when resuming after cancel, the saved step is preserved
  useEffect(() => {
    try {
      const savedStep = sessionStorage.getItem(SESSION_KEYS.CURRENT_STEP);
      
      // Always respect saved step - don't override it
      if (savedStep !== null) {
        const stepIndex = parseInt(savedStep, 10);
        if (!isNaN(stepIndex) && stepIndex >= 0 && stepIndex < steps.length) {
          // Ensure we're on the saved step (in case it was restored earlier)
          setActiveStep(stepIndex);
        }
        return;
      }

      // Only determine step if no saved step exists (fresh edit, not resume)
      if (isEdit && isPending && selectedProductFormData) {
        const { productFGStatuses, materialDetails } = selectedProductFormData;
        const isFileUploadedProduct = checkFileUploadedProduct(selectedProductFormData);
        
        if (isFileUploadedProduct && !(sessionStorage.getItem(SESSION_KEYS.SELECTION_FOR_NEW_PRODUCT))) {
          setActiveStep(0);
          sessionStorage.setItem(SESSION_KEYS.CURRENT_STEP, "0");
        }
        else if (!productFGStatuses?.fgStatusesJSON || Object.keys(productFGStatuses.fgStatusesJSON).length === 0) {
          setActiveStep(1);
          sessionStorage.setItem(SESSION_KEYS.CURRENT_STEP, "1");
        }
        else {
          // If fgStatuses exists, go to review step (matching Commercial Supply behavior)
          setActiveStep(2);
          sessionStorage.setItem(SESSION_KEYS.CURRENT_STEP, "2");
        }
      }
    } catch (error) {
      console.error("Error handling pending product edit:", error);
    }
  }, [isEdit, isPending, selectedProductFormData, steps.length]);

  // Memoize updateStepData to prevent unnecessary re-renders
  const updateStepData = useCallback(
    (stepName, stepData) => {
      try {
        if (isMounted()) {
          setFormData((prevData) => {
            const updatedData = {
              ...prevData,
              [stepName]: stepData,
            };
            try {
              sessionStorage.setItem(
                SESSION_KEYS.FORM_DATA,
                JSON.stringify(updatedData)
              );
            } catch (error) {
              console.error("Error saving to sessionStorage:", error);
            }
            return updatedData;
          });
        }
      } catch (error) {
        console.error(`Error updating step data for ${stepName}:`, error);
      }
    },
    [isMounted]
  );

  const markApiCallSuccessful = useCallback((shouldNavigate = true, nextStepIndex, stepName = null) => {
    const completedSteps = JSON.parse(
      sessionStorage.getItem(SESSION_KEYS.COMPLETED_STEPS) || "[]"
    );
    if (!completedSteps.includes(activeStep)) {
      completedSteps.push(activeStep);
      sessionStorage.setItem(
        SESSION_KEYS.COMPLETED_STEPS,
        JSON.stringify(completedSteps)
      );
    }

    const displayStepName = stepName || steps[activeStep];

    setSnackbar({
      open: true,
      message: `${displayStepName} saved successfully`,
      severity: "success",
    });

    if (shouldNavigate) {
      const nextStep = typeof nextStepIndex === 'number' ? nextStepIndex : activeStep + 1;
      sessionStorage.setItem(SESSION_KEYS.CURRENT_STEP, nextStep.toString());
      setActiveStep(nextStep);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [activeStep, steps]);

  const getStepContent = (step) => {
    if (step === undefined || step === null) {
      console.error("Invalid step value");
      return null;
    }

    const commonProps = {
      formData: formData || {},
      updateStepData: (name, data) => {
        try {
          if (typeof updateStepData === "function") {
            updateStepData(name, data);
          } else {
            console.error("updateStepData is not a function");
          }
        } catch (error) {
          console.error("Error calling updateStepData:", error);
        }
      },
      classes: classes || {},
      readOnly: readOnly,
      handleNext,
      handleBack,
      handleSubmit,
      activeStep,
      stepsLength: steps.length,
      selectedProductFormData: location.state?.selectedProductFormData,
      markApiCallSuccessful,
      fetchFGStatusesAPI: getFetchFGStatusesAPI(apiBaseUrl),
    };

    try {
      switch (step) {
        case 0:
          return <ProductDetails {...commonProps} skipReplicateDialog={skipReplicateDialog} setSkipReplicateDialog={setSkipReplicateDialog} materialDetails={formData?.materialDetails} />;
        case 1:
          return <StatusSelection
            {...commonProps}
            isStepSaved={savedSteps.has(1)}
            onStepSaved={() => setSavedSteps(prev => new Set([...prev, 1]))}
            viewOnlyProductDetails={false}
            isStatusEditMode={false}
          />;
        case 2:
          return <ReviewAndAddProduct {...commonProps} skipReplicateDialog={skipReplicateDialog} setSkipReplicateDialog={setSkipReplicateDialog} setActiveStep={setActiveStep} />;
        default:
          return "Unknown step";
      }
    } catch (error) {
      console.error("Error rendering step content:", error);
      return (
        <div>
          <Typography color="error">
            An error occurred while loading this step. Please try again.
          </Typography>
          <Button
            onClick={() => window.location.reload()}
            variant="outlined"
            color="primary"
          >
            Reload
          </Button>
        </div>
      );
    }
  };

  const handleNext = (stepIndex) => {
    try {
      let nextStep;
      if (typeof stepIndex === 'number') {
        nextStep = stepIndex;
      } else {
        nextStep = activeStep + 1;
      }
      
      const completedSteps = JSON.parse(
        sessionStorage.getItem(SESSION_KEYS.COMPLETED_STEPS) || "[]"
      );
      if (!completedSteps.includes(activeStep)) {
        completedSteps.push(activeStep);
        sessionStorage.setItem(
          SESSION_KEYS.COMPLETED_STEPS,
          JSON.stringify(completedSteps)
        );
      }
      
      sessionStorage.setItem(SESSION_KEYS.CURRENT_STEP, nextStep.toString());

      if (isMounted()) {
        setActiveStep(nextStep);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } catch (error) {
      console.error("Error in handleNext:", error);
      if (isMounted()) {
        setSnackbar({
          open: true,
          message: "An error occurred while proceeding to next step",
          severity: "error",
        });
      }
    }
  };

  const handleBack = () => {
    try {
      if (activeStep === undefined || activeStep <= 0) {
        throw new Error("Already at first step");
      }

      setActiveStep((prevActiveStep) => {
        const newStep = Math.max(0, prevActiveStep - 1);
        sessionStorage.setItem(SESSION_KEYS.CURRENT_STEP, newStep.toString());
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return newStep;
      });
    } catch (error) {
      console.error("Error in handleBack:", error);
      setSnackbar({
        open: true,
        message: "Cannot go back from first step",
        severity: "error",
      });
    }
  };

  const handleSubmit = async () => {
    try {
      setSnackbar({
        open: true,
        message: "Product configuration submitted successfully",
        severity: "success",
      });

      setTimeout(() => {
        // Clear Product Development specific session storage
        Object.values(SESSION_KEYS).forEach(key => {
          sessionStorage.removeItem(key);
        });
        sessionStorage.removeItem("productDetails");
        localStorage.removeItem("fileUploadedProduct");
        localStorage.removeItem("originalCollabrixProductID");

        history.push(
          `/pcmp/Pharma Co/${roleName}/${organizationName}/${ecosystemId}/products`
        );
      }, 2000);
    } catch (error) {
      console.error("Submission error:", error);
      setSnackbar({
        open: true,
        message: error.message || "An error occurred during submission",
        severity: "error",
      });
    }
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleGoBack = () => {
    // Clear Product Development specific session storage
    Object.values(SESSION_KEYS).forEach(key => {
      sessionStorage.removeItem(key);
    });
    sessionStorage.removeItem("productDetails");
    localStorage.removeItem("fileUploadedProduct");
    localStorage.removeItem("originalCollabrixProductID");

    setSnackbar({ open: false, message: '', severity: 'success' });

    history.goBack();
  };

  useEffect(() => {
    setSnackbar({ open: false, message: '', severity: 'success' });
  }, []);

  if (hasErrors) {
    return (
      <Box p={3}>
        <Typography color="error">
          Failed to initialize product configuration
        </Typography>
      </Box>
    );
  }

  if (!isInitialized) {
    return (
      <Box p={3} display="flex" justifyContent="center">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <div className="config-product-stepper-wrapper">
      <Paper className={classes.root}>
        <Box className={classes.stepperHeader}>
          <Typography className={classes.heading} variant="h5">
            Add New Product
          </Typography>
          <Typography className={classes.subheading}>
            Please add the project details and configurations in the fields below.
          </Typography>
          <Box className={`close-page-action`} onClick={handleGoBack}>
            <Typography>Cancel</Typography>
            <CloseIcon />
          </Box>
        </Box>
        <Stepper activeStep={activeStep} className={classes.stepper}>
          {steps.map((label, index) => {
            const stepProps = {};
            const labelProps = {};

            return (
              <Step key={label} {...stepProps}>
                <StepLabel
                  style={{ whiteSpace: "nowrap" }}
                  className={classes.stepLabel}
                  {...labelProps}
                  StepIconProps={{
                    completed: index < activeStep && (
                      <CheckCircleIcon className={classes.stepCompleted} />
                    ),
                  }}
                >
                  {label}
                </StepLabel>
              </Step>
            );
          })}
        </Stepper>
        <div className="stepper-container">{getStepContent(activeStep)}</div>
        <Snackbar
          anchorOrigin={{
            vertical: "top",
            horizontal: "center",
          }}
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={handleCloseSnackbar}
          key={snackbar.message}
          style={{ zIndex: 9999 }}
        >
          <MuiAlert
            elevation={6}
            variant="filled"
            severity={snackbar.severity}
            onClose={handleCloseSnackbar}
            action={
              <IconButton
                size="small"
                color="inherit"
                onClick={handleCloseSnackbar}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            }
          >
            {snackbar.message}
          </MuiAlert>
        </Snackbar>
      </Paper>
    </div>
  );
};

export default function ClinicalProductDevelopmentConfig() {
  return (
    <ClinicalProductDevelopmentErrorBoundary>
      <ClinicalProductDevelopmentStepper />
    </ClinicalProductDevelopmentErrorBoundary>
  );
}

